import React, { useCallback, useEffect, useState } from 'react';
import { documentsAPI } from '../api/client';
import { extractErrorMessage } from '../utils/errors';
import UserSelect from './UserSelect';

/**
 * Contributors on ONE document (GitHub issue #132) -- self-contained, owning
 * its own fetch and API calls (BookmarksPanel's shape rather than
 * DelegationsPanel's presentational one), since there is one caller and no
 * admin counterpart.
 *
 * `canManage` is an AFFORDANCE HINT: the add/remove controls render only when
 * the viewer holds documents.update at this document's node, but the server
 * re-checks independently (contributor_service.assert_may_manage) and is the
 * actual control -- the same relationship every other panel here has to its
 * own `can*` prop.
 *
 * Each row is three independent checkboxes, not a single "contributor" toggle,
 * because the whole point of #132 is that read/comment/edit are separate
 * capabilities -- "similar to owner" is a per-document row, not a blanket grant.
 */
function ContributorsPanel({ documentId, canManage = false }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addingUserId, setAddingUserId] = useState(null);
  const [newCaps, setNewCaps] = useState({ can_read: true, can_comment: true, can_edit: false });
  const [saving, setSaving] = useState(false);

  const fetchRows = useCallback(async () => {
    try {
      const res = await documentsAPI.listContributors(documentId);
      setRows(res.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching contributors:', err);
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const handleAdd = async () => {
    if (!addingUserId) return;
    setSaving(true);
    try {
      await documentsAPI.addContributor(documentId, { user_id: addingUserId, ...newCaps });
      setAddingUserId(null);
      setNewCaps({ can_read: true, can_comment: true, can_edit: false });
      setError(null);
      await fetchRows();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (contributorId) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm('Remove this contributor?')) return;
    try {
      await documentsAPI.removeContributor(documentId, contributorId);
      setError(null);
      await fetchRows();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  if (loading) return null;
  // Renders nothing at all when there is nothing to show and nobody may add
  // one -- a card that only ever says "no contributors" is noise on every
  // document that has none, which is most of them.
  if (rows.length === 0 && !canManage) return null;

  return (
    <div className="card">
      <div className="card-header"><h3>👥 Contributors</h3></div>
      {error && <div className="alert alert-error">{error}</div>}

      {rows.length === 0 ? (
        <p style={{ color: 'var(--muted)', fontSize: '13px' }}>
          No contributors yet. Add someone who should be able to read, comment on, or edit this
          document without a broader location-wide grant.
        </p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Read</th>
              <th>Comment</th>
              <th>Edit</th>
              {canManage && <th></th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>{row.full_name || row.email}</td>
                <td>{row.can_read ? '✓' : ''}</td>
                <td>{row.can_comment ? '✓' : ''}</td>
                <td>{row.can_edit ? '✓' : ''}</td>
                {canManage && (
                  <td>
                    <button
                      type="button"
                      className="btn btn-secondary btn-small"
                      onClick={() => handleRemove(row.id)}
                    >
                      Remove
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {canManage && (
        <div style={{ marginTop: '14px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          {/* GitHub issue #163: the picker was cutting off names -- .multiselect
              has no min-width of its own (shared by every multi-select in the
              app), so this is fixed panel-locally rather than globally. */}
          <div style={{ flex: '1 1 280px', minWidth: '260px' }}>
            <UserSelect value={addingUserId} onChange={setAddingUserId} placeholder="Add a contributor…" />
          </div>
          <label style={{ fontSize: '13px' }}>
            <input
              type="checkbox"
              checked={newCaps.can_read}
              onChange={(e) => setNewCaps((c) => ({ ...c, can_read: e.target.checked }))}
            /> Read
          </label>
          <label style={{ fontSize: '13px' }}>
            <input
              type="checkbox"
              checked={newCaps.can_comment}
              onChange={(e) => setNewCaps((c) => ({ ...c, can_comment: e.target.checked }))}
            /> Comment
          </label>
          <label style={{ fontSize: '13px' }}>
            <input
              type="checkbox"
              checked={newCaps.can_edit}
              onChange={(e) => setNewCaps((c) => ({ ...c, can_edit: e.target.checked }))}
            /> Edit
          </label>
          <button
            type="button"
            className="btn btn-primary btn-small"
            disabled={!addingUserId || saving
              || !(newCaps.can_read || newCaps.can_comment || newCaps.can_edit)}
            onClick={handleAdd}
          >
            {saving ? 'Adding…' : 'Add'}
          </button>
        </div>
      )}
    </div>
  );
}

export default ContributorsPanel;
